import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout';
import { useAuth } from '../context/AuthContext';
import { 
  Settings, 
  Shield,
  Key,
  Bell,
  Mail,
  Database,
  Server,
  Loader2,
  CheckCircle,
  AlertTriangle,
  ExternalLink,
  Copy,
  RefreshCw,
  Lock,
  Smartphone,
  Users,
  Search,
  UserCog
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { toast } from '../lib/toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSettingsPage() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [systemStatus, setSystemStatus] = useState(null);
  const [roleSearchQuery, setRoleSearchQuery] = useState('');
  const [roleSearchResults, setRoleSearchResults] = useState([]);
  const [roleSearchLoading, setRoleSearchLoading] = useState(false);
  const [roleActionLoading, setRoleActionLoading] = useState(false);
  const [admins, setAdmins] = useState([]);

  useEffect(() => {
    fetchSystemStatus();
    if (user?.role === 'superadmin') {
      fetchAdmins();
    }
  }, [token, user]);

  const fetchSystemStatus = async () => {
    setLoading(true);
    try {
      // Fetch stats as a proxy for system status
      const res = await axios.get(`${API_URL}/api/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSystemStatus({
        database: 'connected',
        api: 'running',
        stats: res.data
      });
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('You do not have admin access');
        navigate('/');
      } else {
        setSystemStatus({
          database: 'error',
          api: 'error'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAdmins = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/users?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter to only show admins and superadmins
      const adminUsers = res.data.filter(u => u.role === 'admin' || u.role === 'superadmin');
      setAdmins(adminUsers);
    } catch (err) {
      console.error('Failed to fetch admins:', err);
    }
  };

  const handleRoleSearch = async () => {
    if (!roleSearchQuery.trim()) {
      setRoleSearchResults([]);
      return;
    }
    
    setRoleSearchLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/users?search=${roleSearchQuery}&limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoleSearchResults(res.data);
    } catch (err) {
      toast.error('Search failed');
    } finally {
      setRoleSearchLoading(false);
    }
  };

  const handleSetRole = async (userId, newRole) => {
    const action = newRole === 'admin' ? 'promote to Admin' : 'demote to User';
    if (!window.confirm(`Are you sure you want to ${action}? This change takes effect immediately.`)) {
      return;
    }
    
    setRoleActionLoading(true);
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}/role`, 
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`User role updated to ${newRole}`);
      // Refresh the admins list and search results
      fetchAdmins();
      if (roleSearchQuery) {
        handleRoleSearch();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    } finally {
      setRoleActionLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'superadmin':
        return <span className="px-2 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-bold">SUPERADMIN</span>;
      case 'admin':
        return <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">ADMIN</span>;
      default:
        return <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-600 text-xs">User</span>;
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Settings className="w-6 h-6 text-gray-600" />
              Admin Settings
            </h1>
            <p className="text-gray-500">
              System configuration and status
            </p>
          </div>
          <Button onClick={fetchSystemStatus} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>

        <div className="space-y-6">
          {/* System Status */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Server className="w-5 h-5 text-blue-600" />
              System Status
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 rounded-lg bg-gray-50">
                <div className="flex items-center gap-2 mb-2">
                  <Database className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Database</span>
                </div>
                <div className="flex items-center gap-2">
                  {systemStatus?.database === 'connected' ? (
                    <>
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-green-600 text-sm">Connected</span>
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                      <span className="text-red-600 text-sm">Error</span>
                    </>
                  )}
                </div>
              </div>
              
              <div className="p-4 rounded-lg bg-gray-50">
                <div className="flex items-center gap-2 mb-2">
                  <Server className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">API Server</span>
                </div>
                <div className="flex items-center gap-2">
                  {systemStatus?.api === 'running' ? (
                    <>
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-green-600 text-sm">Running</span>
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                      <span className="text-red-600 text-sm">Error</span>
                    </>
                  )}
                </div>
              </div>
              
              <div className="p-4 rounded-lg bg-gray-50">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Firebase Auth</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-green-600 text-sm">Active</span>
                </div>
              </div>
              
              <div className="p-4 rounded-lg bg-gray-50">
                <div className="flex items-center gap-2 mb-2">
                  <Mail className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Email (Resend)</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-green-600 text-sm">Configured</span>
                </div>
              </div>
            </div>
          </div>

          {/* Authentication Settings */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Lock className="w-5 h-5 text-purple-600" />
              Authentication
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <div>
                  <p className="font-medium text-gray-900">Authentication Provider</p>
                  <p className="text-sm text-gray-500">Firebase Authentication (Google IAM)</p>
                </div>
                <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">Active</span>
              </div>
              
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <div>
                  <p className="font-medium text-gray-900">Email/Password Sign-in</p>
                  <p className="text-sm text-gray-500">Allow users to sign in with email and password</p>
                </div>
                <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">Enabled</span>
              </div>
              
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <div>
                  <p className="font-medium text-gray-900">Google Sign-in</p>
                  <p className="text-sm text-gray-500">Allow users to sign in with Google account</p>
                </div>
                <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">Pending Setup</span>
              </div>
              
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <Smartphone className="w-4 h-4 text-gray-500" />
                  <div>
                    <p className="font-medium text-gray-900">Multi-Factor Authentication (MFA)</p>
                    <p className="text-sm text-gray-500">Managed by Firebase - SMS or Authenticator app</p>
                  </div>
                </div>
                <a 
                  href="https://console.firebase.google.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm"
                >
                  Configure in Firebase
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              
              <div className="flex items-center justify-between py-3">
                <div>
                  <p className="font-medium text-gray-900">Email Verification</p>
                  <p className="text-sm text-gray-500">Require email verification before sign-in</p>
                </div>
                <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">Enabled</span>
              </div>
            </div>
            
            <div className="mt-4 p-4 rounded-lg bg-blue-50 border border-blue-100">
              <p className="text-sm text-blue-800">
                <strong>MFA Note:</strong> Multi-Factor Authentication is managed entirely through Firebase. 
                To enable MFA for users, configure it in your{' '}
                <a 
                  href="https://console.firebase.google.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="underline"
                >
                  Firebase Console
                </a>
                {' '}under Authentication → Sign-in method → Multi-factor authentication.
              </p>
            </div>
          </div>

          {/* Role Configuration */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" />
              Role Configuration
            </h2>
            
            <div className="space-y-3 mb-6">
              <div className="p-4 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">User</span>
                  <span className="px-2 py-1 rounded bg-gray-100 text-gray-600 text-xs">Default</span>
                </div>
                <p className="text-sm text-gray-500">Standard platform access. Can create listings, chat with matches, and manage their own account.</p>
              </div>
              
              <div className="p-4 rounded-lg border border-blue-200 bg-blue-50">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-blue-900">Admin</span>
                  <span className="px-2 py-1 rounded bg-blue-100 text-blue-700 text-xs font-bold">ADMIN</span>
                </div>
                <p className="text-sm text-blue-700">Can view all users, ban/unban accounts, and access admin dashboard. Cannot modify other admin roles.</p>
              </div>
              
              <div className="p-4 rounded-lg border border-purple-200 bg-purple-50">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-purple-900">Superadmin</span>
                  <span className="px-2 py-1 rounded bg-purple-100 text-purple-700 text-xs font-bold">SUPERADMIN</span>
                </div>
                <p className="text-sm text-purple-700">Full platform access. Can promote/demote admins, access all settings, and manage Firebase users directly.</p>
              </div>
            </div>
            
            <div className="text-sm text-gray-500">
              <p>Roles are stored as Firebase Custom Claims and synced to MongoDB for fast access.</p>
            </div>
          </div>

          {/* Role Management - Superadmin Only */}
          {user?.role === 'superadmin' && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <UserCog className="w-5 h-5 text-purple-600" />
                Role Management
                <span className="text-xs font-normal text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full ml-2">Superadmin Only</span>
              </h2>
              
              {/* Current Admins */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Current Administrators</h3>
                {admins.length > 0 ? (
                  <div className="space-y-2">
                    {admins.map((admin) => (
                      <div key={admin.id} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-200">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                            <Shield className="w-4 h-4 text-purple-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 text-sm">{admin.username}</p>
                            <p className="text-xs text-gray-500">{admin.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {getRoleBadge(admin.role)}
                          {admin.role === 'admin' && admin.id !== user?.id && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleSetRole(admin.id, 'user')}
                              disabled={roleActionLoading}
                              className="text-xs h-7 text-red-600 border-red-200 hover:bg-red-50"
                            >
                              Remove Admin
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No administrators found.</p>
                )}
              </div>
              
              {/* Search and Promote */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Promote User to Admin</h3>
                <div className="flex gap-2 mb-3">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Search by email or username..."
                      value={roleSearchQuery}
                      onChange={(e) => setRoleSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleRoleSearch()}
                      className="pl-9 bg-white border-gray-300"
                      data-testid="role-search-input"
                    />
                  </div>
                  <Button 
                    onClick={handleRoleSearch} 
                    disabled={roleSearchLoading}
                    data-testid="role-search-btn"
                  >
                    {roleSearchLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
                  </Button>
                </div>
                
                {roleSearchResults.length > 0 && (
                  <div className="space-y-2">
                    {roleSearchResults.map((u) => (
                      <div key={u.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-200 bg-white">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                            <Users className="w-4 h-4 text-gray-500" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 text-sm">{u.username}</p>
                            <p className="text-xs text-gray-500">{u.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {getRoleBadge(u.role)}
                          {u.role === 'user' && u.id !== user?.id && (
                            <Button
                              size="sm"
                              onClick={() => handleSetRole(u.id, 'admin')}
                              disabled={roleActionLoading}
                              className="text-xs h-7 bg-blue-600 hover:bg-blue-700"
                            >
                              Make Admin
                            </Button>
                          )}
                          {u.role === 'admin' && u.id !== user?.id && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleSetRole(u.id, 'user')}
                              disabled={roleActionLoading}
                              className="text-xs h-7 text-red-600 border-red-200 hover:bg-red-50"
                            >
                              Remove Admin
                            </Button>
                          )}
                          {u.role === 'superadmin' && (
                            <span className="text-xs text-gray-400">Cannot modify</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {roleSearchQuery && roleSearchResults.length === 0 && !roleSearchLoading && (
                  <p className="text-sm text-gray-500">No users found matching &quot;{roleSearchQuery}&quot;</p>
                )}
              </div>
              
              <div className="mt-4 p-4 rounded-lg bg-amber-50 border border-amber-100">
                <p className="text-sm text-amber-800">
                  <strong>⚠️ Important:</strong> Role changes take effect immediately. 
                  Users will need to log out and back in for their new permissions to fully apply.
                </p>
              </div>
            </div>
          )}

          {/* API Information */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-600" />
              API Information
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">API Base URL</label>
                <div className="flex items-center gap-2">
                  <Input 
                    value={API_URL} 
                    readOnly 
                    className="bg-gray-50 font-mono text-sm"
                  />
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => copyToClipboard(API_URL)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Admin Endpoints</label>
                <div className="bg-gray-50 rounded-lg p-3 font-mono text-xs text-gray-600 space-y-1">
                  <p>GET  /api/admin/stats - Platform statistics</p>
                  <p>GET  /api/admin/users - List users</p>
                  <p>PUT  /api/admin/users/:id/role - Update role</p>
                  <p>POST /api/admin/users/:id/ban - Ban user</p>
                  <p>POST /api/admin/users/:id/unban - Unban user</p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          {systemStatus?.stats && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Database className="w-5 h-5 text-green-600" />
                Database Overview
              </h2>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 rounded-lg bg-gray-50">
                  <p className="text-2xl font-bold text-gray-900">{systemStatus.stats.total_users || 0}</p>
                  <p className="text-xs text-gray-500">Total Users</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-gray-50">
                  <p className="text-2xl font-bold text-gray-900">{systemStatus.stats.total_wtb || 0}</p>
                  <p className="text-xs text-gray-500">Want Listings</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-gray-50">
                  <p className="text-2xl font-bold text-gray-900">{systemStatus.stats.total_inventory || 0}</p>
                  <p className="text-xs text-gray-500">Inventory Items</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-gray-50">
                  <p className="text-2xl font-bold text-gray-900">{systemStatus.stats.total_matches || 0}</p>
                  <p className="text-xs text-gray-500">Matches</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
